import type { ProgrammeReportVendor } from '../../lib/api'
import { BAND_COLOR } from '../charts'
import { SEVERITIES } from './AgeingBars'

// The vendors carrying the most risk, as the API ordered them: the most severe
// band first and the weakest control score first within it.
//
// The band is a swatch beside its word, and the open findings are four
// columns headed by a severity swatch and its name, so identity is never the
// colour alone and every number is text in the table's own ink, lined up on
// tabular figures.

const BAND_WORD: Record<string, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  INSUFFICIENT_EVIDENCE: 'Insufficient',
}

const SHORT: Record<string, string> = { critical: 'Crit', high: 'High', medium: 'Med', low: 'Low' }

// "Tier 1 Critical" reads as "Tier 1" in the column; the band beside it says
// the rest, and the full label is the cell's tooltip.
function shortTier(label: string): string {
  return /^(Tier \d)/.exec(label)?.[1] ?? label
}

export function TopVendorsTable({ vendors, dateOf }: { vendors: ProgrammeReportVendor[]; dateOf: (iso: string) => string }) {
  return (
    <div className="rpt-table-wrap">
      <table className="rpt-table">
        <thead>
          <tr>
            <th scope="col">Vendor</th>
            <th scope="col">Tier</th>
            <th scope="col">Band</th>
            <th scope="col" className="n">
              Score
            </th>
            {SEVERITIES.map((s) => (
              <th key={s.key} scope="col" className="n sev" title={'Open ' + s.label.toLowerCase() + ' findings'}>
                <i className="rpt-swatch sm" style={{ background: s.color }} />
                {SHORT[s.key]}
              </th>
            ))}
            <th scope="col">Last scored</th>
            <th scope="col">Owner</th>
          </tr>
        </thead>
        <tbody>
          {vendors.map((v) => (
            <tr key={v.vendorId}>
              <td className="rpt-vendor">
                {v.href ? (
                  <a className="rpt-strong" href={v.href}>
                    {v.name}
                  </a>
                ) : (
                  <span className="rpt-strong">{v.name}</span>
                )}
                <span className="rpt-code">{v.code}</span>
              </td>
              <td className="nowrap" title={v.tierLabel}>
                {shortTier(v.tierLabel)}
              </td>
              <td className="nowrap" title={v.band === 'INSUFFICIENT_EVIDENCE' ? 'Insufficient evidence' : undefined}>
                <i className="rpt-swatch" style={{ background: BAND_COLOR[v.band] }} />
                {BAND_WORD[v.band] ?? v.band}
              </td>
              <td className="n">{v.score == null ? '-' : v.score.toFixed(1)}</td>
              {SEVERITIES.map((s) => (
                <td key={s.key} className={'n sev' + (v.openFindings[s.key] === 0 ? ' zero' : '')}>
                  {v.openFindings[s.key]}
                </td>
              ))}
              <td className="nowrap">{v.lastAssessedAt ? dateOf(v.lastAssessedAt) : '-'}</td>
              <td>{v.owner || <span className="rpt-quiet">Not recorded</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
